/* eslint-disable */
import { useDispatch, useSelector } from 'react-redux';
import { SelectPicker } from 'rsuite';
import {
    useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import {
    Eye, Pencil, Upload
} from 'lucide-react';
import { Button } from '@mui/material';
import { modalActions } from '@/Actions';
import VariantTable from '@/Pages/Food Products/VariantPrice/VariantTable';
import { BaseModal } from '../BaseModal';
import inventoryAnalyticsServices from '@/Services/Analytics Services/inventory-analytics.services';
import GlobalSearch from '@/Components/globalSearch/search';
import { notificationServices } from '@/Services';
import {
    getCurrentDateInfo, handleExport, header, monthsColumns, PriceInput, shortMonthNames
} from './helperPriceUpdate';
import ReviewAndSummarize from './ReviewAndSummarize';
import Tab from '@/Components/InfoTab/tab';
import { skuTypesListWithId, useProductData } from '@/Helpers';
import ConfirmationModal from '../MultiPoModal/ConfirmationModal';
import { useAuth } from '@/Components/Auth/Login/auth';

const sortInfo = {
    itemName: {
        type: 'string',
        path: 'obj?.itemName',
    },
};

function RmPriceUpdateModal() {
    const { show } = useSelector((state) => state.modal.rmPriceModal);
    const productList = useSelector((state) => state.form.ALL_PRODUCTS.data.LIST);
    const storeList = useSelector((state) => state.form.STORES.data.LIST);
    const hasVariants = useMemo(() => productList?.every((item) => item?.variants?.length > 0), [productList]);
    const dispatch = useDispatch();
    const { userConfig = {}, appConfig } = useAuth();
    const { daysLimit = 5 } = appConfig?.configs ?? {};
    const oldPrice = useRef([]);
    const { level } = userConfig?.userDetails || {};

    const [tableData, setTableData] = useState([]);
    const [selectedStore, setSelectedStore] = useState(null);
    const [searchValue, setSearchValue] = useState('');
    const [nextStep, setNextStep] = useState(0);
    const [summarizeData, setSummarizeData] = useState([]);
    const [selectSkuType, setSelectSkuType] = useState('Ingredient');
    const [isLoading, setIsLoading] = useState(false);
    const [validation, setValidation] = useState(false);
    const [confirmationModal, setConfirmationModal] = useState({
        isOpen: false,
        message: '',
        heading: '',
        isAlert: false,
        alertType: 'info',
        customButton: [],
        showCrossBtn: false,
    });

    const [tableColumns, setTableColumns] = useState(monthsColumns());
    const [showPrevMonth, setShowPrevMonth] = useState(false);

    useEffect(() => {
        if (!hasVariants) {
            useProductData(dispatch);
        }
    }, [hasVariants]);

    const monthKeys = useMemo(() => {
        return tableColumns.filter((col) => /^[0-9]+$/.test(col.key)).map((col) => col.key);
    }, [tableColumns]);

    const getProductDetails = useCallback((productId, variantId) => {
        if (!productList || productList.length === 0) {
            return { product: null, variant: null };
        }


        const productIdNum = Number(productId);
        const variantIdNum = Number(variantId);

        const product = productList.find((item) => {
            const itemId = Number(item.id);
            return itemId === productIdNum || String(item.id) === String(productId);
        });

        const variant = product?.variants?.find((item) => {
            const itemId = Number(item.id);
            return itemId === variantIdNum || String(item.id) === String(variantId);
        });

        return { product, variant };
    }, [productList]);

    const getTableColumns = useCallback((comingMonths) => {
        if (!comingMonths || comingMonths.length === 0) {
            return monthsColumns();
        }

        const firstMonth = Math.min(...comingMonths) - 1;
        const date = new Date();
        const currentMonth = date.getMonth();
        const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
        const currentYear = currentMonth === 0 ? date.getFullYear() - 1 : date.getFullYear();
        const months = [];
        const selectedMonths = [];

        for (let m = firstMonth; m <= prevMonth; m++) {
            selectedMonths.push(m);
        }

        selectedMonths.reverse().forEach((monthIndex) => {
            const title = `${shortMonthNames[monthIndex]?.toUpperCase() || ''} ${currentYear}`;
            months.push({
                title,
                key: String(monthIndex),
                customCol: true,
                ...(prevMonth === monthIndex ? { coloured: true } : {}),
            });
        });

        return [
            ...header,
            ...months,
        ];
    }, []);

    const getTableData = useCallback((rawData) => {
        if (!rawData || Object.keys(rawData).length === 0) {
            setTableData([]);
            oldPrice.current = [];
            return;
        }

        if (!productList || productList.length === 0) {
            console.warn('ProductList is empty or not loaded');
            setTableData([]);
            return;
        }

        const list = [];
        const seenProducts = new Map();
        let skippedCount = 0;
        let processedCount = 0;

        Object.entries(rawData).forEach(([key, value]) => {
            if (!Array.isArray(value)) return;

            const month = parseInt(key.split('-')[1], 10);
            if (isNaN(month)) return;

            const monthKey = String(month - 1);

            value.forEach((item) => {
                processedCount++;
                const { product, variant } = getProductDetails(item?.productId, item?.variantId);

                if (!product || !variant) {
                    skippedCount++;
                    // Only log in development
                    if (process.env.NODE_ENV === 'development') {
                        console.warn(`Product or variant not found:`, {
                            productId: item?.productId,
                            variantId: item?.variantId,
                        });
                    }
                    return;
                }

                const compositeKey = `${item.productId}_${item.variantId}`;
                let existingRow = seenProducts.get(compositeKey);

                if (!existingRow) {
                    existingRow = {
                        id: compositeKey,
                        sno: list.length + 1,
                        skuType: product?.skuType ?? '',
                        productId: item.productId,
                        variantId: item.variantId,
                        unit: product?.unit ?? '',
                        itemName: `${product?.productName ?? ''} ${variant?.displayName ? `(${variant?.displayName})` : ''}`.trim(),
                        skuCode: variant?.clientVariantId ?? '',
                        hasChanged: false,
                        changeMonth: [],
                    };

                    monthKeys.forEach((mk) => {
                        existingRow[mk] = '';
                    });

                    list.push(existingRow);
                    seenProducts.set(compositeKey, existingRow);
                }

                if (monthKeys.includes(monthKey)) {
                    const price = parseFloat(item?.price);
                    existingRow[monthKey] = isNaN(price) ? '' : price.toFixed(2);
                }
            });
        });

        oldPrice.current = JSON.parse(JSON.stringify(list));
        setTableData(list);

        if (process.env.NODE_ENV === 'development') {
            console.log('Table data processing:', {
                processed: processedCount,
                skipped: skippedCount,
                created: list.length,
            });
        }
    }, [getProductDetails, monthKeys, productList]);

    const handleCloseConfirmation = useCallback(() => {
        setConfirmationModal({
            isOpen: false,
            message: '',
            heading: '',
            isAlert: false,
            alertType: 'info',
            showCrossBtn: false,
            customButton: [],
        });
    }, []);

    const resetModalState = useCallback(() => {
        setSelectedStore(null);
        setTableData([]);
        setSearchValue('');
        setSelectSkuType('Ingredient');
        setNextStep(0);
        setSummarizeData([]);
        setTableColumns(monthsColumns());
        setShowPrevMonth(false);
        oldPrice.current = [];
    }, []);

    const handleClose = useCallback(() => {
        dispatch(modalActions.toggleRmPriceModal(false));
        resetModalState();
    }, [dispatch, resetModalState]);

    const handleSave = useCallback(async () => {
        if (!selectedStore) {
            notificationServices?.generateNotification({
                type: 'error',
                message: 'Please select a store',
            });
            return;
        }

        setIsLoading(true);
        try {
            const { currentMonth, currentYear } = getCurrentDateInfo();
            const payload = [];
            const currentMonthPrices = [];

            tableData.forEach((item) => {
                if (!item?.hasChanged || !item?.changeMonth?.length) return;

                monthKeys?.forEach((month) => {
                    const price = item[month];
                    const monthNum = Number(month);
                    let year = currentYear;

                    if (monthNum > currentMonth) {
                        year = currentYear - 1;
                    }

                    if (!price || price === '' || price === '0' || price === '0.00') {
                        return;
                    }

                    if (item.changeMonth.includes(String(month))) {
                        payload.push({
                            productId: item.productId,
                            variantId: item.variantId,
                            price: parseFloat(price).toFixed(2),
                            month: monthNum + 1,
                            year,
                        });
                    }
                });
            });
            payload.forEach((item) => {
                let month = null;
                let year = new Date().getFullYear();
                if (item?.month === 12) {
                    month = 1
                    year = new Date().getFullYear() + 1;
                }
                else {
                    month = item?.month + 1;
                }
                currentMonthPrices.push({
                    productId: item.productId,
                    variantId: item.variantId,
                    price: item.price,
                    month: month,
                    year: year,
                });
            });
            if (payload.length === 0) {
                notificationServices?.generateNotification({
                    type: 'warning',
                    message: 'No changes to save',
                });
                setIsLoading(false);
                return;
            }

            const finalData = {
                storeId: selectedStore,
                monthWisePrices: [...payload, ...currentMonthPrices],
            };

            const response = await inventoryAnalyticsServices.saveRmPriceMonthWise({
                data: finalData,
            });

            if (response?.statusCode === 200) {
                notificationServices?.generateNotification({
                    type: 'success',
                    message: response?.message || 'Price updated successfully',
                });
                handleCloseConfirmation();
                handleClose();
            } else {
                throw new Error(response?.message || 'Failed to save prices');
            }
        } catch (error) {
            notificationServices?.generateNotification({
                type: 'error',
                message: error?.message ?? 'An error occurred while saving prices',
            });
        } finally {
            setIsLoading(false);
        }
    }, [tableData, selectedStore, monthKeys, handleClose, handleCloseConfirmation]);


    const handleConfirmPrice = useCallback(() => {
        const { currentMonth, currentYear } = getCurrentDateInfo();
        const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
        const previousYear = currentMonth === 0 ? currentYear - 1 : currentYear;

        setConfirmationModal({
            isOpen: true,
            heading: 'Update Production Cost',
            message: `Please note that updating the ${shortMonthNames[prevMonth]} ${previousYear} price will automatically update the ${shortMonthNames[currentMonth]} ${currentYear} price, along with the production costs for both months`,
            handleConfirmSave: handleSave,
            handleCloseConfirmation: handleCloseConfirmation,
        });
    }, [handleCloseConfirmation, handleSave]);

    const handleReviewAndSummarize = useCallback(() => {
        const filteredData = tableData.filter(
            (item) => item?.hasChanged &&
                item?.changeMonth?.length > 0 &&
                (item?.skuType?.toLowerCase() === selectSkuType?.toLowerCase() || selectSkuType === 'ALL')
        );

        if (filteredData.length === 0) {
            notificationServices?.generateNotification({
                type: 'error',
                message: 'No changes made to the price',
            });
            return;
        }

        const updatedData = filteredData.map((obj) => {
            const oldPriceRecord = oldPrice.current.find(
                (item) => item?.productId === obj?.productId && item?.variantId === obj?.variantId
            );

            if (!oldPriceRecord) {
                return null;
            }

            const findChangeMonth = obj.changeMonth.find((month) =>
                Object.keys(oldPriceRecord).includes(month)
            );

            if (!findChangeMonth) {
                return null;
            }

            return {
                itemName: obj?.itemName || '',
                skuCode: obj?.skuCode || '',
                [`oldPrice_${findChangeMonth}`]: oldPriceRecord[findChangeMonth] || '',
                [`newPrice_${findChangeMonth}`]: obj[findChangeMonth] || '',
                changeMonth: findChangeMonth,
                unit: obj?.unit || '',
            };
        }).filter(Boolean); // Remove null entries

        if (updatedData.length === 0) {
            notificationServices?.generateNotification({
                type: 'error',
                message: 'No valid changes found to summarize',
            });
            return;
        }

        setSummarizeData(updatedData);
        setNextStep(1);
    }, [tableData, selectSkuType]);

    const handleValidate = useCallback(async () => {
        const { currentMonth, currentYear } = getCurrentDateInfo();
        const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
        const previousYear = currentMonth === 0 ? currentYear - 1 : currentYear;
        setIsLoading(true);
        try {
            const response = await inventoryAnalyticsServices.validateRmPriceMonthWise({
                data: {
                    storeId: selectedStore,
                    month: prevMonth + 1,
                    year: previousYear,
                    validate: true,
                },
            });
            if (response?.statusCode === 200) {
                setValidation(response?.validation);
                notificationServices?.generateNotification({
                    type: 'success',
                    message: response?.message ?? 'Price validated successfully',
                });
            } else {
                throw new Error(response?.message ?? 'Failed to validate price');
            }
        } catch (error) {
            notificationServices?.generateNotification({
                type: 'error',
                message: error?.message ?? 'An error occurred while validating price',
            });
        } finally {
            setIsLoading(false);
        }
    }, [selectedStore, validation]);

    const renderBtns = useMemo(() => {
        const currentDate = new Date().getDate();
        return () => (nextStep === 0 ?
            currentDate <= daysLimit ? (
                <div className='w-full flex justify-end'>
                    <Button
                        className='primary-btn h-[50px] w-[300px] text-[18px] border !border-amber-700'
                        onClick={handleReviewAndSummarize}
                        disabled={isLoading}
                    >
                        Review & Summarize
                    </Button>
                </div>
            ) : (
                !validation ? (
                    <div className='w-full flex justify-end'>
                        <Button
                            className='primary-btn h-[50px] w-[300px] text-[18px] border !border-amber-700'
                            onClick={handleValidate}
                            disabled={isLoading}
                        >
                            Validate Price
                        </Button>
                    </div>
                ) : null
            ) : (
                <div className='w-full flex justify-end'>
                    <Button
                        className='primary-btn h-[50px] w-[300px] text-[18px] border !border-amber-700'
                        onClick={handleConfirmPrice}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Saving...' : 'Confirm & Save'}
                    </Button>
                </div>
            ));
    }, [nextStep, handleReviewAndSummarize, handleConfirmPrice, handleValidate, isLoading, validation]);

    const renderInputFields = useCallback((month) => {
        return (product) => (
            <PriceInput
                product={product}
                month={month}
                onUpdate={handleChange}
                daysLimit={daysLimit}
            />
        );
    }, [daysLimit]);

    const handleChange = useCallback((value, month, id) => {
        setTableData((prev) => prev.map((item) => {
            if (item.id !== id) return item;

            const existing = item?.changeMonth || [];
            let updatedChangeMonths;

            if (!value || value === '') {
                updatedChangeMonths = existing.filter((m) => m !== month);

                return {
                    ...item,
                    [month]: '',
                    changeMonth: updatedChangeMonths,
                    hasChanged: updatedChangeMonths.length > 0,
                };
            }

            updatedChangeMonths = existing.includes(month)
                ? existing
                : [...existing, month];

            const numValue = parseFloat(value);
            return {
                ...item,
                [month]: isNaN(numValue) ? '' : numValue.toFixed(2),
                changeMonth: updatedChangeMonths,
                hasChanged: updatedChangeMonths.length > 0,
            };
        }));
    }, []);

    const customRowData = useMemo(() => {
        const base = {
            sno: (product) => (
                <div className='text-gray-700 text-left text-[12px]'>{product?.sno || ''}</div>
            ),
            itemName: (product) => (
                <div className='text-gray-700 text-left text-[12px]'>{product?.itemName || ''}</div>
            ),
            skuCode: (product) => (
                <div className='text-gray-700 text-left text-[12px]'>{product?.skuCode || ''}</div>
            ),
        };

        monthKeys.forEach((month) => {
            base[month] = renderInputFields(month);
        });

        return base;
    }, [renderInputFields, monthKeys]);

    const customCol = useCallback((column) => {
        return <div className='text-gray-700 text-left text-nowrap text-[12px]'>{column?.title || ''}</div>;
    }, []);

    const fetchPriceData = useCallback(async (value) => {
        if (!value) return;
        if (!productList || productList.length === 0) {
            notificationServices?.generateNotification({
                type: 'warning',
                message: 'Product list is not loaded. Please wait...',
            });
            return;
        }

        const notificationId = notificationServices?.generateNotification({
            type: 'loading',
            message: 'Fetching price data...',
        });

        setIsLoading(true);

        try {
            const res = await inventoryAnalyticsServices.getRmPriceMonthWise({
                data: { storeId: value },
            });
            if (res?.statusCode === 200) {
                setValidation(res?.validation);
                const monthArray = Object.keys(res?.data || {}).map(
                    (key) => parseInt(key.split('-')[1], 10)
                ).filter((num) => !isNaN(num));

                const computedColumns = getTableColumns(monthArray);
                setTableColumns(computedColumns);
                getTableData(res?.data);

                notificationServices?.generateNotification({
                    type: 'success',
                    message: 'Price data fetched successfully',
                    config: { id: notificationId },
                });
            } else {
                throw new Error(res?.message || 'Failed to fetch price data');
            }
        } catch (error) {
            notificationServices?.generateNotification({
                type: 'error',
                message: error?.message ?? 'An error occurred while fetching price data',
                config: { id: notificationId },
            });
        } finally {
            setIsLoading(false);
        }
    }, [getTableData, getTableColumns, productList]);


    const handleStoreChange = useCallback((value) => {
        setSelectedStore(value);
        if (value) {
            fetchPriceData(value);
        }
    }, [fetchPriceData]);

    const generatedTableData = useMemo(() => {
        if (!tableData || tableData.length === 0) {
            return [];
        }
        let filteredData = tableData;

        if (selectSkuType && selectSkuType !== 'ALL') {
            filteredData = filteredData.filter(
                (item) => item?.skuType?.toString().toLowerCase() === selectSkuType?.toString().toLowerCase()
            );
        }

        if (!searchValue || typeof searchValue !== 'string') {
            return filteredData;
        }

        const searchLower = searchValue.toLowerCase().trim();
        return filteredData.filter((item) => {
            const itemName = item?.itemName?.toLowerCase() || '';
            const skuCode = item?.skuCode?.toLowerCase() || '';
            return itemName.includes(searchLower) || skuCode.includes(searchLower);
        });
    }, [tableData, searchValue, selectSkuType]);

    const modalHeading = useMemo(() => {
        return nextStep === 0 ? 'Setting RM prices for last month' : 'Review & Summarize';
    }, [nextStep]);

    return (
        <BaseModal
            heading={modalHeading}
            open={show}
            handleClose={handleClose}
            renderBtns={renderBtns}
            width='100%'
            height='100%'
            disableBackdropClick
            disableEscapeKeyDown
        >
            <ConfirmationModal
                isOpen={confirmationModal?.isOpen}
                heading={confirmationModal.heading}
                message={confirmationModal.message}
                onClose={confirmationModal?.handleCloseConfirmation}
                onConfirm={confirmationModal?.handleConfirmSave}
                isAlert={confirmationModal?.isAlert}
                alertType={confirmationModal?.alertType}
                customButton={confirmationModal?.customButton}
                showCrossBtn={confirmationModal?.showCrossBtn}
                textAlign
            />
            {nextStep === 0 ? (
                <div className='flex flex-col gap-3'>
                    <div className='flex flex-wrap md:flex-nowrap items-center gap-2 px-2 w-full max-w-full bg-white pt-2 pb-2'>
                        <div className='flex-shrink-0 min-w-[180px] max-w-full flex items-center'>
                            <span className='text-gray-700 text-left whitespace-nowrap mr-2 text-sm font-medium'>
                                Select Store:
                            </span>
                            <div style={{ minWidth: 160, maxWidth: '100%' }}>
                                <SelectPicker
                                    data={(storeList ?? []).map((item) => ({
                                        label: item?.name ?? '',
                                        value: item?.id ?? '',
                                    }))}
                                    placeholder='Select Store'
                                    value={selectedStore}
                                    style={{
                                        width: '100%',
                                        minWidth: 300,
                                        maxWidth: 500,
                                        fontSize: 14,
                                    }}
                                    cleanable={false}
                                    menuStyle={{
                                        width: 320,
                                        zIndex: 4000,
                                    }}
                                    virtualized
                                    onChange={handleStoreChange}
                                    disabled={isLoading}
                                />
                            </div>
                        </div>
                        <div className='flex-1 min-w-0 max-w-full flex items-center'>
                            <GlobalSearch
                                placeholderText='Search by Item Name or SKU Code'
                                setSearchValue={setSearchValue}
                                searchDropDown={false}
                            />
                        </div>
                        <Tab
                            tabList={skuTypesListWithId.map((item) => ({
                                label: item.title,
                                value: item.displayName,
                            }))}
                            tabValue={selectSkuType}
                            handleTabChange={setSelectSkuType}
                            activeTabColor='bg-yellow-200'
                        />
                        <Button
                            className='flex justify-center gap-2 px-4 py-2 text-white bg-teal-600 border border-gray-300 rounded-md hover:bg-teal-700'
                            onClick={() => handleExport(generatedTableData, monthKeys)}
                            disabled={!generatedTableData?.length}
                        >
                            <Upload size={16} />
                            <span className='font-bold normal-case'>Export (.csv)</span>
                        </Button>
                    </div>
                    {(!productList || productList?.length === 0) ? (
                        <div className='flex flex-col items-center justify-center h-64'>
                            <p className='text-gray-500 text-lg'>No products found</p>
                            <button
                                onClick={() => useProductData(dispatch)}
                                className='mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700'
                                type='button'
                            >
                                Retry Loading
                            </button>
                        </div>
                    ) : (
                        <div className='overflow-y-auto h-[calc(100vh-250px)]'>
                            {(!tableData?.length) ? (
                                <div className='flex items-center justify-center h-full'>
                                    <div className='text-gray-500'>
                                        {selectedStore
                                            ? 'No price data available for this store'
                                            : 'Please select a store to view price data'}
                                    </div>
                                </div>
                            ) : (
                                <VariantTable
                                    columns={tableColumns}
                                    data={generatedTableData}
                                    customRowData={customRowData}
                                    customCol={customCol}
                                    sortValueInfo={sortInfo}
                                    virtualization
                                />
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <ReviewAndSummarize
                    data={summarizeData}
                    setNextStep={setNextStep}
                    selectedStore={selectedStore}
                    storeList={storeList}
                    showPrevMonth={showPrevMonth}
                    setShowPrevMonth={setShowPrevMonth}
                />
            )}
        </BaseModal>
    );
}

export default RmPriceUpdateModal;
